<template>
  <VModal :showing="showing" container-classes="max-w-lg w-full" @update:showing="$emit('update:showing', $event)">
    <template #header>
      <h2 class="text-lg mb-0">Create URL Slide</h2>
      <p class="text-sm text-gray-600 font-normal mb-0">Internal Only</p>
    </template>

    <form class="flex flex-col gap-8 mb-8" data-testid="create-url-slide-form" @submit.prevent="createUrlSlide">
      <label class="font-normal text-sm">
        Slide Title
        <VInput
          v-model="title"
          v-bind="titleAttrs"
          required
          class="mt-2"
          placeholder="Give your slide a title"
          :error="!!errors.title"
          :maxlength="60"
        />
        <div v-if="!!errors.title" class="text-sm text-red-800">{{ errors.title }}</div>
      </label>

      <label class="font-normal text-sm">
        URL
        <VInput
          v-model="url"
          v-bind="urlAttrs"
          label="URL"
          required
          class="mt-2"
          placeholder="https://dynamic.vrify.com/revenue"
          :error="!!errors.url"
          :maxlength="120"
        />
        <div v-if="!!errors.url" class="text-sm text-red-800">{{ errors.url }}</div>
      </label>

      <input class="hidden" type="submit" />
    </form>

    <template #footer>
      <div class="flex justify-end gap-2">
        <VButton data-testid="cancel-btn" size="md" @click="$emit('update:showing', false)">Cancel</VButton>
        <VButton variant="copper" data-testid="create-btn" size="md" type="submit" @click="createUrlSlide">Create</VButton>
      </div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'
import ZodMessages from '@/zod_presets/messages'

import type { Deck } from '@/entities/deck'
import type { EditDeck } from '@/composables/useEditDeck'

const props = defineProps({
  showing: {
    type: Boolean,
    required: true,
  },
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
})

const emit = defineEmits(['update:showing', 'add-slide'])

const validationSchema = toTypedSchema(
  z.object({
    title: z.string().nonempty(ZodMessages.title).default(''),
    url: z
      .string()
      .regex(/^https?:\/\//, { message: 'URL must start with http or https' })
      .refine(
        url => {
          try {
            return !new URL(url).hostname.includes('vrify') || !new RegExp('/decks/').test(url)
          } catch (e) {
            return false
          }
        },
        { message: 'VRIFY 3D Presentation URLs are not supported. Please try another URL.' }
      )
      .default(''),
  })
)

const { handleSubmit, resetForm, errors, defineField, isFieldTouched, setFieldTouched } = useForm({ validationSchema })
const [title, titleAttrs] = defineField('title')
const [url, urlAttrs] = defineField('url')

const createUrlSlide = handleSubmit(values => {
  try {
    emit('add-slide', {
      type: 'D',
      deck_id: props.deck.id,
      title: title.value,
      content_url: url.value,
    })
  } finally {
    resetForm()
  }
})
</script>
